// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api, { fetchUserProfileApi } from '../services/api'; // Adjust path if needed

const AuthContext = createContext(null);

const SESSION_TIMEOUT_DURATION = 60 * 60 * 91000; // 20 minutes in milliseconds

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('authToken'));
    const [loading, setLoading] = useState(true); // Start true: we are initially loading/checking auth state

    const logout = useCallback(() => {
        console.log("AuthContext: Logging out user.");
        localStorage.removeItem('authToken');
        localStorage.removeItem('loginTimestamp');
        setToken(null); // This will trigger the useEffect below
        setUser(null);
        // setLoading(false); // Let the useEffect handle setting loading to false after logout actions
    }, []);

    useEffect(() => {
        console.log("AuthContext useEffect triggered. Current token state:", token);
        let sessionTimeoutId = null; // Initialize to null

        const checkSessionTimeout = () => {
            const loginTimestamp = localStorage.getItem('loginTimestamp');
            if (token && loginTimestamp) { // Only check if still logged in (token exists)
                const now = Date.now();
                if (now - parseInt(loginTimestamp, 10) > SESSION_TIMEOUT_DURATION) {
                    console.log("Session expired due to inactivity.");
                    alert("Your session has expired due to inactivity. Please log in again.");
                    logout(); // This will change token, re-triggering useEffect
                }
            } else if (sessionTimeoutId) { // If token became null, clear interval
                clearInterval(sessionTimeoutId);
                sessionTimeoutId = null;
            }
        };

        const loadUserAndStartSession = async () => {
            setLoading(true); // Indicate we are starting the loading process

            const currentTokenFromStorage = localStorage.getItem('authToken');
            const loginTimestamp = localStorage.getItem('loginTimestamp');

            if (currentTokenFromStorage) {
                if (token !== currentTokenFromStorage) { // Sync state if localStorage changed externally
                    setToken(currentTokenFromStorage);
                    // The effect will re-run due to token state change, so we can return early
                    // or let it continue, but it's cleaner to let the re-run handle it.
                    // For this cycle, we might still proceed to avoid infinite loops if not careful.
                }

                if (!loginTimestamp || (Date.now() - parseInt(loginTimestamp, 10) > SESSION_TIMEOUT_DURATION)) {
                    console.log("AuthContext: Session expired on load or timestamp missing for token:", currentTokenFromStorage);
                    logout(); // This updates token to null, effect will re-run
                    // setLoading(false) will be handled in the next run of useEffect when token is null
                    return; // Exit this async function execution
                }

                try {
                    console.log("AuthContext: Token exists, attempting to fetch user profile.");
                    const { data } = await fetchUserProfileApi(); // Uses token from api interceptor
                    console.log("AuthContext: User profile fetched:", data);
                    setUser(data); // User is set
                    if (sessionTimeoutId) clearInterval(sessionTimeoutId); // Clear previous interval if any
                    sessionTimeoutId = setInterval(checkSessionTimeout, 60 * 1000); // Start new interval
                } catch (error) {
                    console.error("AuthContext: Failed to load user, logging out.", error);
                    logout(); // User is set to null, token to null
                }
            } else {
                console.log("AuthContext: No token found, ensuring user and token are null.");
                setUser(null); // Explicitly ensure user is null if no token
                if (token !== null) setToken(null); // Sync token state if it wasn't already null
                if (sessionTimeoutId) { // Clear interval if user logs out
                    clearInterval(sessionTimeoutId);
                    sessionTimeoutId = null;
                }
            }
            setLoading(false); // Set loading false AFTER all attempts to set/nullify user & token
        };

        loadUserAndStartSession();

        return () => {
            if (sessionTimeoutId) {
                clearInterval(sessionTimeoutId);
            }
        };
    }, [token, logout]); // Key dependencies: re-run when token state changes or logout function identity changes

    const login = (newToken, userDataFromServer) => {
        console.log("AuthContext: login function called. User data:", userDataFromServer);
        const now = Date.now();
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('loginTimestamp', now.toString());
        setUser(userDataFromServer); // Set user data
        setToken(newToken);        // Set token LAST - this will trigger the useEffect
        console.log("AuthContext: User & Token state updated by login function.");
    };


    return (
        <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!token, loading }}>
            {/* Only render children when not in the initial loading phase.
                If loading is true, ProtectedRoute or individual pages can show their own spinners.
                Or, you can show a global spinner here.
                The critical part is that `isAuthenticated` and `user` are stable before children try to use them.
            */}
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);